import React, { useState, useEffect } from 'react';
import { 
  User, 
  Building, 
  MapPin, 
  FileText, 
  Settings,
  Edit3,
  Save,
  Camera,
  CheckCircle,
  AlertCircle,
  Clock,
  Mail,
  Phone,
  Globe,
  Calendar,
  Upload,
  Eye,
  X,
  CreditCard,
  Hash
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { ProfessionalInput, ProfessionalTextarea, ProfessionalSelect } from '../../components/common/ProfessionalInput';
import DocumentUpload from '../../components/common/DocumentUpload';
import ProfilePictureUpload from '../../components/common/ProfilePictureUpload';
import { getDocumentValue } from '../../utils/documentUtils';

const ProfileOptimized = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sellerData, setSellerData] = useState(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [missingFields, setMissingFields] = useState([]);

  // Form data for different sections
  const [formData, setFormData] = useState({
    // Personal Info
    fullName: '',
    email: '',
    phone: '',
    alternatePhone: '',
    dateOfBirth: '',
    profileImage: '',
    
    // Business Info
    businessName: '',
    businessType: '',
    yearEstablished: '',
    description: '',
    website: '',
    gstNumber: '',
    panNumber: '',
    
    // Address
    registeredAddress: {
      street: '',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    warehouseAddress: {
      isSameAsRegistered: true,
      street: '',
      landmark: '',
      city: '',
      state: '',
      pincode: ''
    },
    
    // Bank Details
    bankDetails: {
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      branchName: ''
    },
    
    // Documents
    documents: {},
    
    // Categories
    selectedCategories: []
  });

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'business', label: 'Business Info', icon: Building },
    { id: 'address', label: 'Address & Bank', icon: MapPin },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Calculate completion percentage
  const calculateCompletion = (data) => {
    const requiredFields = [
      'fullName', 'email', 'phone', 'businessName', 'businessType', 'gstNumber', 'panNumber',
      'registeredAddress.street', 'registeredAddress.city', 'registeredAddress.state', 'registeredAddress.pincode',
      'bankDetails.accountHolderName', 'bankDetails.accountNumber', 'bankDetails.ifscCode'
    ];
    
    const optionalFields = [
      'alternatePhone', 'dateOfBirth', 'yearEstablished', 'description', 'website',
      'registeredAddress.landmark', 'bankDetails.bankName', 'bankDetails.branchName'
    ];

    let filledRequired = 0;
    let filledOptional = 0;
    const missing = [];

    requiredFields.forEach(field => {
      const value = getNestedValue(data, field);
      if (value && value.toString().trim()) {
        filledRequired++;
      } else {
        missing.push(field);
      }
    });

    optionalFields.forEach(field => {
      const value = getNestedValue(data, field);
      if (value && value.toString().trim()) {
        filledOptional++;
      }
    });

    // Categories
    if (data.selectedCategories && data.selectedCategories.length > 0) {
      filledRequired++;
    } else {
      missing.push('selectedCategories');
    }

    const totalRequired = requiredFields.length + 1; // +1 for categories
    const totalOptional = optionalFields.length;
    
    // Required fields count for 80%, optional for 20%
    const requiredPercentage = (filledRequired / totalRequired) * 80;
    const optionalPercentage = (filledOptional / totalOptional) * 20;
    
    const total = Math.round(requiredPercentage + optionalPercentage);
    
    setCompletionPercentage(total);
    setMissingFields(missing);
    
    return total;
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current && current[key], obj);
  };

  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((current, key) => {
      if (!current[key]) current[key] = {};
      return current[key];
    }, obj);
    target[lastKey] = value;
  };

  // Load seller data
  useEffect(() => {
    const loadSellerData = async () => {
      try {
        setLoading(true);
        
        // Try to fetch complete seller profile from API
        try {
          const response = await api.get('/seller/profile');
          if (response.data.success) {
            const sellerData = response.data.data;
            
            const completeData = {
              // Personal Info
              fullName: sellerData.fullName || user?.fullName || '',
              email: sellerData.email || user?.email || '',
              phone: sellerData.phone || user?.phone || '',
              alternatePhone: sellerData.alternatePhone || '',
              dateOfBirth: sellerData.dateOfBirth || '',
              profileImage: sellerData.profileImage || '',
              
              // Business Info
              businessName: sellerData.businessName || user?.businessName || '',
              businessType: sellerData.businessType || user?.businessType || '',
              yearEstablished: sellerData.yearEstablished || '',
              description: sellerData.description || '',
              website: sellerData.website || '',
              gstNumber: sellerData.gstNumber || user?.gstNumber || '',
              panNumber: sellerData.panNumber || user?.panNumber || '',
              
              // Address
              registeredAddress: sellerData.registeredAddress || {
                street: '',
                landmark: '',
                city: '',
                state: '',
                pincode: '',
                country: 'India'
              },
              warehouseAddress: sellerData.warehouseAddress || {
                isSameAsRegistered: true,
                street: '',
                landmark: '',
                city: '',
                state: '',
                pincode: ''
              },
              
              // Bank Details
              bankDetails: sellerData.bankDetails || {
                accountHolderName: '',
                accountNumber: '',
                ifscCode: '',
                bankName: '',
                branchName: ''
              },
              
              // Documents
              documents: sellerData.documents || {},
              
              // Categories
              selectedCategories: sellerData.selectedCategories || user?.selectedCategories || []
            };
            
            setFormData(completeData);
            setSellerData(completeData);
            calculateCompletion(completeData);
          }
        } catch (apiError) {
          console.log('API fetch failed, using user data:', apiError);
          
          // Fallback to user data if API fails
          if (user) {
            const fallbackData = {
              ...formData,
              fullName: user.fullName || '',
              email: user.email || '',
              phone: user.phone || '',
              businessName: user.businessName || '',
              businessType: user.businessType || '',
              gstNumber: user.gstNumber || '',
              panNumber: user.panNumber || '',
              registeredAddress: user.registeredAddress || formData.registeredAddress,
              warehouseAddress: user.warehouseAddress || formData.warehouseAddress,
              bankDetails: user.bankDetails || formData.bankDetails,
              documents: user.documents || formData.documents,
              selectedCategories: user.selectedCategories || []
            };
            setFormData(fallbackData);
            setSellerData(fallbackData);
            calculateCompletion(fallbackData);
          }
        }
      } catch (error) {
        console.error('Error loading seller data:', error);
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    loadSellerData();
  }, [user]);

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData };
    setNestedValue(newFormData, field, value);
    setFormData(newFormData);
    calculateCompletion(newFormData);
  };

  const handleSave = async (section) => {
    try {
      setSaving(true);
      
      // Here you would call the appropriate API endpoint
      // await api.put('/seller/profile', formData);
      
      toast.success(`${section} updated successfully!`);
      setSellerData(formData);
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 border-green-500';
    if (percentage >= 50) return 'text-yellow-600 border-yellow-500';
    return 'text-red-600 border-red-500';
  };

  const renderPersonalInfo = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
        <button
          onClick={() => handleSave('Personal Information')}
          disabled={saving}
          className="btn btn-primary flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Profile Picture Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h4 className="text-md font-medium text-gray-800 mb-4 flex items-center">
          <Camera className="w-5 h-5 mr-2 text-primary-600" />
          Profile Picture
        </h4>
        <div className="flex items-center space-x-6">
          <ProfilePictureUpload
            currentImage={formData.profileImage}
            onImageChange={(imageData) => {
              if (imageData) {
                handleInputChange('profileImage', imageData.previewUrl);
              } else {
                handleInputChange('profileImage', '');
              }
            }}
            size="large"
          />
          <div className="flex-1">
            <p className="text-sm text-gray-600 mb-2">
              Upload a professional profile picture to help customers recognize your business.
            </p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• Recommended size: 400x400 pixels</li>
              <li>• Accepted formats: JPEG, PNG, WebP</li>
              <li>• Maximum file size: 5MB</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProfessionalInput
          label="Full Name"
          type="text"
          value={formData.fullName}
          onChange={(e) => handleInputChange('fullName', e.target.value)}
          placeholder="Enter your full name"
          icon={User}
          required
        />

        <ProfessionalInput
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          placeholder="Enter your email"
          icon={Mail}
          required
        />

        <ProfessionalInput
          label="Phone Number"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          placeholder="Enter your phone number"
          icon={Phone}
          required
        />

        <ProfessionalInput
          label="Alternate Phone"
          type="tel"
          value={formData.alternatePhone}
          onChange={(e) => handleInputChange('alternatePhone', e.target.value)}
          placeholder="Enter alternate phone number"
          icon={Phone}
        />

        <ProfessionalInput
          label="Date of Birth"
          type="date"
          value={formData.dateOfBirth}
          onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
          icon={Calendar}
        />
      </div>
    </div>
  );

  const renderBusinessInfo = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Business Information</h3>
        <button
          onClick={() => handleSave('Business Information')}
          disabled={saving}
          className="btn btn-primary flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProfessionalInput
          label="Business Name"
          type="text"
          value={formData.businessName}
          onChange={(e) => handleInputChange('businessName', e.target.value)}
          placeholder="Enter your business name"
          icon={Building}
          required
        />

        <ProfessionalSelect
          label="Business Type"
          value={formData.businessType}
          onChange={(e) => handleInputChange('businessType', e.target.value)}
          placeholder="Select business type"
          options={[
            'Sole Proprietorship',
            'Partnership',
            'Private Limited',
            'LLP',
            'Public Limited'
          ]}
          required
        />

        <ProfessionalInput
          label="GST Number"
          type="text"
          value={formData.gstNumber}
          onChange={(e) => handleInputChange('gstNumber', e.target.value)}
          placeholder="Enter GST number"
          icon={Hash}
          required
        />

        <ProfessionalInput
          label="PAN Number"
          type="text"
          value={formData.panNumber}
          onChange={(e) => handleInputChange('panNumber', e.target.value)}
          placeholder="Enter PAN number"
          icon={CreditCard}
          required
        />

        <ProfessionalInput
          label="Year Established"
          type="number"
          value={formData.yearEstablished}
          onChange={(e) => handleInputChange('yearEstablished', e.target.value)}
          placeholder="Enter year established"
          icon={Calendar}
        />

        <ProfessionalInput
          label="Website"
          type="url"
          value={formData.website}
          onChange={(e) => handleInputChange('website', e.target.value)}
          placeholder="Enter website URL"
          icon={Globe}
        />

        <div className="md:col-span-2">
          <ProfessionalTextarea
            label="Business Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe your business"
            rows={4}
          />
        </div>
      </div>
    </div>
  );

  const renderAddressAndBank = () => (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Address & Bank Details</h3>
        <button
          onClick={() => handleSave('Address & Bank Details')}
          disabled={saving}
          className="btn btn-primary flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Registered Address */}
      <div>
        <h4 className="text-md font-medium text-gray-800 mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-primary-600" />
          Registered Address
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <ProfessionalInput
              label="Street Address"
              type="text"
              value={formData.registeredAddress.street}
              onChange={(e) => handleInputChange('registeredAddress.street', e.target.value)}
              placeholder="Enter street address"
              icon={MapPin}
              required
            />
          </div>
          
          <ProfessionalInput
            label="City"
            type="text"
            value={formData.registeredAddress.city}
            onChange={(e) => handleInputChange('registeredAddress.city', e.target.value)}
            placeholder="Enter city"
            required
          />

          <ProfessionalInput
            label="State"
            type="text"
            value={formData.registeredAddress.state}
            onChange={(e) => handleInputChange('registeredAddress.state', e.target.value)}
            placeholder="Enter state"
            required
          />

          <ProfessionalInput
            label="Pincode"
            type="text"
            value={formData.registeredAddress.pincode}
            onChange={(e) => handleInputChange('registeredAddress.pincode', e.target.value)}
            placeholder="Enter pincode"
            required
          />
        </div>
      </div>

      {/* Bank Details */}
      <div>
        <h4 className="text-md font-medium text-gray-800 mb-4 flex items-center">
          <CreditCard className="w-5 h-5 mr-2 text-primary-600" />
          Bank Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProfessionalInput
            label="Account Holder Name"
            type="text"
            value={formData.bankDetails.accountHolderName}
            onChange={(e) => handleInputChange('bankDetails.accountHolderName', e.target.value)}
            placeholder="Enter account holder name"
            icon={User}
            required
          />

          <ProfessionalInput
            label="Account Number"
            type="text"
            value={formData.bankDetails.accountNumber}
            onChange={(e) => handleInputChange('bankDetails.accountNumber', e.target.value)}
            placeholder="Enter account number"
            icon={Hash}
            required
          />

          <ProfessionalInput
            label="IFSC Code"
            type="text"
            value={formData.bankDetails.ifscCode}
            onChange={(e) => handleInputChange('bankDetails.ifscCode', e.target.value)}
            placeholder="Enter IFSC code"
            icon={CreditCard}
            required
          />

          <ProfessionalInput
            label="Bank Name"
            type="text"
            value={formData.bankDetails.bankName}
            onChange={(e) => handleInputChange('bankDetails.bankName', e.target.value)}
            placeholder="Enter bank name"
            icon={Building}
          />
        </div>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Documents</h3>
        <button
          onClick={() => handleSave('Documents')}
          disabled={saving}
          className="btn btn-primary flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DocumentUpload
          label="GST Certificate"
          description="Upload your GST registration certificate"
          acceptedTypes=".pdf,.jpg,.jpeg,.png"
          maxSize={5}
          value={getDocumentValue(formData.documents, 'gstCertificate')}
          onChange={(file) => handleInputChange('documents.gstCertificate', file)}
          required
        />

        <DocumentUpload
          label="PAN Card"
          description="Upload your PAN card copy"
          acceptedTypes=".pdf,.jpg,.jpeg,.png"
          maxSize={5}
          value={getDocumentValue(formData.documents, 'panCard')}
          onChange={(file) => handleInputChange('documents.panCard', file)}
          required
        />

        <DocumentUpload
          label="Aadhar Card"
          description="Upload your Aadhar card copy"
          acceptedTypes=".pdf,.jpg,.jpeg,.png"
          maxSize={5}
          value={getDocumentValue(formData.documents, 'aadharCard')}
          onChange={(file) => handleInputChange('documents.aadharCard', file)}
        />

        <DocumentUpload
          label="Cancelled Cheque"
          description="Upload a cancelled cheque for bank verification"
          acceptedTypes=".pdf,.jpg,.jpeg,.png"
          maxSize={5}
          value={getDocumentValue(formData.documents, 'cancelledCheque')}
          onChange={(file) => handleInputChange('documents.cancelledCheque', file)}
        />

        <DocumentUpload
          label="Business License"
          description="Upload your business license (if applicable)"
          acceptedTypes=".pdf,.jpg,.jpeg,.png"
          maxSize={5}
          value={getDocumentValue(formData.documents, 'businessLicense')}
          onChange={(file) => handleInputChange('documents.businessLicense', file)}
        />

        <DocumentUpload
          label="Trade License"
          description="Upload your trade license (if applicable)"
          acceptedTypes=".pdf,.jpg,.jpeg,.png"
          maxSize={5}
          value={getDocumentValue(formData.documents, 'tradeLicense')}
          onChange={(file) => handleInputChange('documents.tradeLicense', file)}
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <FileText className="h-5 w-5 text-blue-400 mt-0.5" />
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">Document Guidelines</h4>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>All documents should be clear and readable</li>
                <li>Accepted formats: PDF, JPG, JPEG, PNG</li>
                <li>Maximum file size: 5MB per document</li>
                <li>GST Certificate and PAN Card are mandatory</li>
                <li>Documents will be verified within 24-48 hours</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Account Settings</h3>
      </div>
      
      <div className="text-center py-8 text-gray-500">
        <Settings className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p>Account settings will be implemented here</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Profile Completion */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Profile Picture Upload with Completion Ring */}
                <div className="relative">
                  <div className={`rounded-full border-4 ${getCompletionColor(completionPercentage)} p-1`}>
                    <ProfilePictureUpload
                      currentImage={formData.profileImage}
                      onImageChange={(imageData) => {
                        if (imageData) {
                          handleInputChange('profileImage', imageData.previewUrl);
                        } else {
                          handleInputChange('profileImage', '');
                        }
                      }}
                      size="medium"
                    />
                  </div>
                </div>

                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {formData.fullName || 'Complete Your Profile'}
                  </h1>
                  <p className="text-gray-600">{formData.email}</p>
                  <div className="flex items-center mt-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          completionPercentage >= 80 ? 'bg-green-500' : 
                          completionPercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${completionPercentage}%` }}
                      />
                    </div>
                    <span className={`text-sm font-medium ${getCompletionColor(completionPercentage)}`}>
                      {completionPercentage}% Complete
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  completionPercentage >= 80 ? 'bg-green-100 text-green-800' :
                  completionPercentage >= 50 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {completionPercentage >= 80 ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Profile Complete
                    </>
                  ) : completionPercentage >= 50 ? (
                    <>
                      <Clock className="w-4 h-4 mr-1" />
                      Almost Done
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Needs Attention
                    </>
                  )}
                </div>
                {missingFields.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {missingFields.length} fields remaining
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'personal' && renderPersonalInfo()}
            {activeTab === 'business' && renderBusinessInfo()}
            {activeTab === 'address' && renderAddressAndBank()}
            {activeTab === 'documents' && renderDocuments()}
            {activeTab === 'settings' && renderSettings()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileOptimized;
